/**
 * Channel Info Sidebar - Shows channel details, members, pinned messages, etc.
 */

import React, { useState } from 'react';
import { Tabs, List, Avatar, Typography, Button, Input, Empty, Badge, Tag, Divider, Modal, Select, message } from 'antd';
import { 
  UserOutlined, 
  StarOutlined, 
  PushpinOutlined, 
  SearchOutlined,
  SettingOutlined,
  InfoCircleOutlined,
  TeamOutlined,
  UserAddOutlined
} from '@ant-design/icons';
import { useQuery, useQueryClient } from 'react-query';
import api from '../../config/api';
import { useChatWebSocket } from '../../contexts/ChatWebSocketContext';
import { PresenceIndicator } from './PresenceIndicator';
import './ChannelInfoSidebar.css';

const { Text, Title } = Typography;
const { TabPane } = Tabs;

interface ChannelInfoSidebarProps {
  channelId: string;
}

type PresenceStatus = 'online' | 'away' | 'busy' | 'offline';

const normalizeStatus = (status: string | undefined): PresenceStatus => {
  if (status === 'online' || status === 'away' || status === 'busy' || status === 'offline') {
    return status;
  }
  return 'offline';
};

export const ChannelInfoSidebar: React.FC<ChannelInfoSidebarProps> = ({ channelId }) => {
  const { presence } = useChatWebSocket();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [inviteModalVisible, setInviteModalVisible] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [inviting, setInviting] = useState(false);

  // Fetch all users for invite
  const { data: allUsersData } = useQuery(
    'chat-all-users',
    () => api.get('/api/chat/users').then(res => res.data),
    {
      enabled: inviteModalVisible,
    }
  );

  // Fetch channel details
  const { data: channelData } = useQuery(
    ['channel-details', channelId],
    () => api.get(`/api/chat/channels/${channelId}`).then(res => res.data),
    {
      enabled: !!channelId,
    }
  );

  // Fetch channel members
  const { data: membersData } = useQuery(
    ['channel-members', channelId],
    () => api.get(`/api/chat/channels/${channelId}/members`).then(res => res.data),
    {
      enabled: !!channelId,
    }
  );

  // Fetch pinned messages
  const { data: pinnedData } = useQuery(
    ['pinned-messages', channelId],
    () => api.get(`/api/chat/channels/${channelId}/pinned`).then(res => res.data),
    {
      enabled: !!channelId,
    }
  );

  const channel = channelData?.channel;
  const members = membersData?.members || [];
  const pinnedMessages = pinnedData?.messages || [];
  const allUsers = allUsersData?.users || [];

  // Filter out users who are already members
  const availableUsers = allUsers.filter((user: any) => 
    !members.some((member: any) => member.id === user.id)
  );

  const handleInviteUsers = async () => {
    if (selectedUserIds.length === 0) {
      message.warning('Please select at least one user');
      return;
    }

    setInviting(true);
    try {
      await api.post(`/api/chat/channels/${channelId}/members`, {
        userIds: selectedUserIds,
      });
      message.success(`Invited ${selectedUserIds.length} user(s) to the channel`);
      queryClient.invalidateQueries(['channel-members', channelId]);
      setInviteModalVisible(false);
      setSelectedUserIds([]);
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to invite users');
    } finally {
      setInviting(false);
    }
  };

  const filteredMembers = members.filter((member: any) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const firstName = member.first_name || member.firstName || '';
    const lastName = member.last_name || member.lastName || '';
    const email = member.email || '';
    return `${firstName} ${lastName} ${email}`.toLowerCase().includes(query);
  });

  const getMemberStatus = (member: any) => {
    const userPresence = presence[member.id] || null;
    return userPresence?.status 
      ? normalizeStatus(userPresence.status)
      : normalizeStatus(member.presence_status || member.presenceStatus);
  };

  return (
    <div className="channel-info-sidebar">
      <div className="channel-info-header">
        <Title level={5} style={{ margin: 0, padding: '16px', borderBottom: '1px solid #e8e8e8' }}>
          {(() => {
            if (!channel) return 'Channel Info';
            if (channel.type === 'direct') {
              // For direct messages, show the other user's name
              const otherMember = members.find((m: any) => {
                const firstName = m.first_name || m.firstName || '';
                const lastName = m.last_name || m.lastName || '';
                return `${firstName} ${lastName}`.trim() || m.email;
              });
              if (otherMember) {
                const firstName = otherMember.first_name || otherMember.firstName || '';
                const lastName = otherMember.last_name || otherMember.lastName || '';
                return `${firstName} ${lastName}`.trim() || otherMember.email || 'Direct Message';
              }
              return 'Direct Message';
            }
            return channel.name || 'Channel Info';
          })()}
        </Title>
      </div>

      <Tabs defaultActiveKey="about" className="channel-info-tabs">
        <TabPane tab={<span><InfoCircleOutlined /> About</span>} key="about">
          <div className="channel-info-content">
            {channel?.description && (
              <div style={{ marginBottom: 16 }}>
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                  Description
                </Text>
                <Text>{channel.description}</Text>
              </div>
            )}
            
            <Divider style={{ margin: '16px 0' }} />
            
            <div style={{ marginBottom: 16 }}>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                Channel Type
              </Text>
              <Tag color={channel?.is_private || channel?.isPrivate ? 'red' : 'blue'}>
                {channel?.is_private || channel?.isPrivate ? 'Private' : 'Public'}
              </Tag>
            </div>

            <div style={{ marginBottom: 16 }}>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                Created
              </Text>
              <Text>
                {channel?.created_at 
                  ? new Date(channel.created_at).toLocaleDateString()
                  : 'Unknown'}
              </Text>
            </div>

            <Divider style={{ margin: '16px 0' }} />

            <Button type="default" block icon={<SettingOutlined />}>
              Channel Settings
            </Button>
          </div>
        </TabPane>

        <TabPane tab={<span><TeamOutlined /> Members ({members.length})</span>} key="members">
          <div className="channel-info-content">
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <Input
                placeholder="Search members..."
                prefix={<SearchOutlined />}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                style={{ flex: 1 }}
              />
              <Button
                type="primary"
                icon={<UserAddOutlined />}
                size="small"
                onClick={() => setInviteModalVisible(true)}
              >
                Invite
              </Button>
            </div>
            
            <List
              dataSource={filteredMembers}
              renderItem={(member: any) => {
                const firstName = member.first_name || member.firstName || '';
                const lastName = member.last_name || member.lastName || '';
                const displayName = `${firstName} ${lastName}`.trim() || member.email || 'Unknown';
                const status = getMemberStatus(member);
                
                return (
                  <List.Item style={{ padding: '8px 0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <PresenceIndicator status={status} size="small">
                        <Avatar size="small" src={member.avatar_url} icon={<UserOutlined />} />
                      </PresenceIndicator>
                      <Text style={{ marginLeft: 8, flex: 1 }}>{displayName}</Text>
                    </div>
                  </List.Item>
                );
              }}
            />
          </div>
        </TabPane>

        <TabPane tab={<span><PushpinOutlined /> Pinned ({pinnedMessages.length})</span>} key="pinned">
          <div className="channel-info-content">
            {pinnedMessages.length === 0 ? (
              <Empty 
                description="No pinned messages" 
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                style={{ marginTop: 40 }}
              />
            ) : (
              <List
                dataSource={pinnedMessages}
                renderItem={(msg: any) => {
                  const firstName = msg.user?.first_name || msg.user?.firstName || '';
                  const lastName = msg.user?.last_name || msg.user?.lastName || '';
                  const userName = `${firstName} ${lastName}`.trim() || msg.user?.email || 'Unknown';
                  
                  return (
                    <List.Item style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                      <div style={{ width: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                          <Text strong style={{ fontSize: 12 }}>{userName}</Text>
                          <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
                            {msg.created_at ? new Date(msg.created_at).toLocaleString() : ''}
                          </Text>
                        </div>
                        <Text style={{ fontSize: 13 }}>{msg.text || msg.content}</Text>
                      </div>
                    </List.Item>
                  );
                }}
              />
            )}
          </div>
        </TabPane>

        <TabPane tab={<span><StarOutlined /> Saved</span>} key="saved">
          <div className="channel-info-content">
            <Empty 
              description="No saved messages" 
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              style={{ marginTop: 40 }}
            />
          </div>
        </TabPane>
      </Tabs>

      <Modal
        title="Invite Users to Channel"
        open={inviteModalVisible}
        onOk={handleInviteUsers}
        onCancel={() => {
          setInviteModalVisible(false);
          setSelectedUserIds([]);
        }}
        okText="Invite"
        confirmLoading={inviting}
      >
        <Select
          mode="multiple"
          placeholder="Select users to invite"
          value={selectedUserIds}
          onChange={setSelectedUserIds}
          style={{ width: '100%' }}
          showSearch
          filterOption={(input, option) => {
            const label = typeof option?.label === 'string' ? option.label : String(option?.label || '');
            return label.toLowerCase().includes(input.toLowerCase());
          }}
          options={availableUsers.map((user: any) => {
            const firstName = user.first_name || user.firstName || '';
            const lastName = user.last_name || user.lastName || '';
            const displayName = `${firstName} ${lastName}`.trim() || user.email || 'Unknown';
            return {
              label: displayName,
              value: user.id,
            };
          })}
        />
      </Modal>
    </div>
  );
};

